//* 지도사 계정 일괄 발급 — 행 파싱·검증 (소속은 폼 공통값이라 여기선 계정 4칸만 다룬다)

export type BulkWorkerRowStatus = 'idle' | 'pending' | 'done' | 'error';

export interface BulkWorkerRow {
  key: string;
  username: string;
  password: string;
  name: string;
  phoneNumber: string; // 숫자만
  status: BulkWorkerRowStatus;
  error?: string;
}

let rowSeq = 0;
export const makeBulkWorkerRow = (
  init?: Partial<Pick<BulkWorkerRow, 'username' | 'password' | 'name' | 'phoneNumber'>>,
): BulkWorkerRow => {
  rowSeq += 1;
  return {
    key: `bulk-worker-${Date.now()}-${rowSeq}`,
    username: init?.username?.trim() ?? '',
    password: init?.password?.trim() ?? '',
    name: init?.name?.trim() ?? '',
    phoneNumber: onlyDigits(init?.phoneNumber ?? ''),
    status: 'idle',
  };
};

export const onlyDigits = (v: string) => v.replace(/[^0-9]/g, '');

export const isBlankRow = (row: BulkWorkerRow) =>
  !row.username && !row.password && !row.name && !row.phoneNumber;

export const BULK_WORKER_COLUMNS = ['username', 'password', 'name', 'phoneNumber'] as const;
export type BulkWorkerColumn = typeof BULK_WORKER_COLUMNS[number];

//* 엑셀·구글시트 복사본은 탭 구분 — 여러 칸/여러 줄을 붙여넣을 때만 표 채우기로 처리
export const isGridPaste = (text: string) => /[\t\n]/.test(text.trim());

//* 붙여넣은 칸을 표에 펼친다. (rowIndex, column) 칸부터 오른쪽·아래로 채우고 모자라면 행을 늘린다.
//* 발급완료 행은 건드리지 않고 건너뛴다. 첫 칸 붙여넣기의 첫 줄이 머리글(아이디/ID)이면 버린다.
export const applyGridPaste = (
  rows: BulkWorkerRow[],
  rowIndex: number,
  column: BulkWorkerColumn,
  text: string,
): { rows: BulkWorkerRow[]; filled: number } => {
  const colStart = BULK_WORKER_COLUMNS.indexOf(column);
  let lines = text
    .replace(/\r/g, '')
    .split('\n')
    .filter((line) => line.trim() !== '')
    .map((line) => line.split('\t').map((cell) => cell.trim()));
  if (colStart === 0 && /^(아이디|id|username)$/i.test(lines[0]?.[0] ?? '')) {
    lines = lines.slice(1);
  }

  const next = [...rows];
  let cursor = rowIndex;
  lines.forEach((cells) => {
    while (next[cursor]?.status === 'done') cursor += 1;
    const base = next[cursor] ?? makeBulkWorkerRow();
    const patch: Partial<BulkWorkerRow> = { status: 'idle', error: undefined };
    cells.forEach((cell, i) => {
      const field = BULK_WORKER_COLUMNS[colStart + i];
      if (!field) return;
      patch[field] = field === 'phoneNumber' ? onlyDigits(cell).slice(0, 11) : cell;
    });
    next[cursor] = { ...base, ...patch };
    cursor += 1;
  });
  return { rows: next, filled: lines.length };
};

export interface ExistingWorkerLike {
  username?: string;
  info?: { phoneNumber?: string };
}

export interface BulkWorkerRowError {
  field: BulkWorkerColumn;
  message: string;
  //* 중복·형식 오류는 입력 즉시(live) 보여주고, 빈칸 필수는 [발급]을 누른 뒤에만 보여준다
  live: boolean;
}

//* 행별 입력 오류 (key → 오류). 빈 행은 무시(행 번호는 표 번호와 맞추려고 전체 rows를 받는다). 이미 발급된(done) 행은 검사하지 않되 중복 대조엔 포함한다.
//* takenUsernames: 서버 전체 계정 대조 결과(다른 상조·다른 역할 포함) — username 인덱스가 unique가 아니라
//* 서버는 중복 아이디를 거부하지 않고 그대로 만든다. 그래서 여기서 막아야 한다.
export const validateBulkWorkerRows = (
  rows: BulkWorkerRow[],
  existingWorkers: ExistingWorkerLike[],
  takenUsernames: ReadonlySet<string> = new Set(),
): Record<string, BulkWorkerRowError> => {
  const errors: Record<string, BulkWorkerRowError> = {};
  const existingUsernames = new Set(
    existingWorkers.map((w) => w.username).filter((v): v is string => !!v),
  );
  const existingPhones = new Set(
    existingWorkers
      .map((w) => onlyDigits(w.info?.phoneNumber ?? ''))
      .filter((v) => v && v !== '010'),
  );

  const usernameRows = new Map<string, number[]>();
  const phoneCount = new Map<string, number>();
  rows.forEach((row, idx) => {
    if (isBlankRow(row)) return;
    if (row.username) {
      usernameRows.set(row.username, [...(usernameRows.get(row.username) ?? []), idx + 1]);
    }
    if (row.phoneNumber && row.phoneNumber !== '010') {
      phoneCount.set(row.phoneNumber, (phoneCount.get(row.phoneNumber) ?? 0) + 1);
    }
  });

  rows.forEach((row, idx) => {
    if (isBlankRow(row) || row.status === 'done') return;
    const set = (field: BulkWorkerColumn, message: string, live: boolean) => {
      errors[row.key] = { field, message, live };
    };
    const sameRows = (usernameRows.get(row.username) ?? []).filter((n) => n !== idx + 1);
    if (!row.username) set('username', '아이디 필수', false);
    else if (existingUsernames.has(row.username) || takenUsernames.has(row.username))
      set('username', '이미 사용', true);
    else if (sameRows.length > 0)
      set('username', `아이디 중복 (${sameRows.join(', ')}행)`, true);
    else if (!row.password) set('password', '비밀번호 필수', false);
    else if (!row.name) set('name', '이름 필수', false);
    else if (
      row.phoneNumber &&
      row.phoneNumber !== '010' &&
      (row.phoneNumber.length < 10 || row.phoneNumber.length > 11)
    )
      set('phoneNumber', '휴대폰 번호 형식 오류', false);
    else if (existingPhones.has(row.phoneNumber))
      set('phoneNumber', '이미 등록된 휴대폰 번호', true);
    else if ((phoneCount.get(row.phoneNumber) ?? 0) > 1)
      set('phoneNumber', '목록 안 번호 중복', true);
  });
  return errors;
};

//* 서버 거부 사유 — 취약 비밀번호·400 message만 노출(그 외 코드는 전역 필터가 지운다)
export const bulkWorkerCreateErrorMessage = (err: unknown): string => {
  const status = (err as any)?.response?.status;
  const message = (err as any)?.response?.data?.message;
  if (status === 400 && typeof message === 'string' && message) return message;
  if (status === 400 && Array.isArray(message) && message.length) return String(message[0]);
  return '발급 실패';
};
